"use client";

import { FormControl } from "@mui/base";
import { Autocomplete, TextField } from "@mui/material";
import React from "react";
import SalifyFallback from "../../salify-fallback/default";
import { useCardSectionContext } from "./context";
import clientFetcher from "src/lib/fetcher/client";

const CartLocationControls = (
  props: React.PropsWithChildren<{
    form?: boolean;
    controlPrefix?: string;
    readOnly?: boolean;
    values?: any;
    errors?: any;
    touched?: any;
    handleBlur?: any;
    handleChange?: any;
    setFieldValue?: any;
  }>,
) => {
  const { provinces, updateFormValue, formValue: _formValue, districts } = useCardSectionContext();
  const [readOnly] = React.useState(!!props.readOnly);
  const [districtOptions, setDistrictOptions] = React.useState<any[]>([]);
  const [wardOptions, setWardOptions] = React.useState<any[]>([]);
  const formValue = props.values ?? _formValue;
  const controlPrefix = props.controlPrefix ?? "shipping";

  React.useEffect(() => {
    (async () => {
      const districtId = formValue[`${controlPrefix}_district`];
      if (districtId) {
        clientFetcher.getLocationWards({ districtId: districtId }).then((res) =>
          setWardOptions(
            res.map(({ id, fullName, locationDistrictId: parentId }) => ({
              id,
              label: fullName,
              parentId,
            })),
          ),
        );
      }
    })();
  }, [formValue[`${controlPrefix}_district`]]);

  React.useEffect(() => {
    setDistrictOptions(
      districts.filter(({ parentId }) => parentId === formValue[`${controlPrefix}_province`]),
    );
  }, [districts, formValue[`${controlPrefix}_province`]]);

  const handleChangeProvince = (id: string) => {
    if (props.form) {
      props.setFieldValue(`${controlPrefix}_province`, id);
      props.setFieldValue(`${controlPrefix}_district`, undefined);
      props.setFieldValue(`${controlPrefix}_ward`, undefined);
    } else {
      updateFormValue({
        shipping_province: id,
        shipping_district: undefined,
        shipping_ward: undefined,
      });
    }

    setDistrictOptions(districts.filter(({ parentId }) => parentId === id));
  };

  const handleChangeDistrict = (id: string) => {
    if (props.form) {
      props.setFieldValue(`${controlPrefix}_district`, id);
      props.setFieldValue(`${controlPrefix}_ward`, undefined);
    } else {
      updateFormValue({ shipping_district: id, shipping_ward: undefined });
    }
  };

  const handleChangeWard = (id: string) => {
    if (props.form) {
      props.setFieldValue(`${controlPrefix}_ward`, id);
    } else {
      updateFormValue({ shipping_ward: id });
    }
  };

  const provinceValue = React.useMemo(() => {
    return (
      (formValue[`${controlPrefix}_province`] &&
        provinces.find(({ id }) => id === formValue[`${controlPrefix}_province`])) ||
      {}
    );
  }, [provinces, formValue[`${controlPrefix}_province`]]);

  const districtValue = React.useMemo(() => {
    return (
      (formValue[`${controlPrefix}_district`] &&
        districts.find(({ id }) => id === formValue[`${controlPrefix}_district`])) ||
      {}
    );
  }, [districts, formValue[`${controlPrefix}_district`]]);

  const wardValue = React.useMemo(() => {
    return (
      (formValue[`${controlPrefix}_ward`] &&
        wardOptions.find(({ id }) => id === formValue[`${controlPrefix}_ward`])) ||
      {}
    );
  }, [wardOptions, formValue[`${controlPrefix}_ward`]]);

  return (
    <>
      <FormControl className="col-span-2 lg:col-span-1">
        <SalifyFallback.Label
          path="Provinces"
          default="Tỉnh thành"
          render={(label) => (
            <Autocomplete
              options={provinces}
              fullWidth
              readOnly={readOnly}
              disableClearable
              getOptionLabel={(option) => option?.label ?? ""}
              value={provinceValue}
              onChange={(_, { id }: any) => handleChangeProvince(id)}
              renderInput={(params: any) => (
                <TextField
                  inputProps={{ readOnly }}
                  {...params}
                  {...(props.form
                    ? {
                        error:
                          !!props.touched[`${controlPrefix}_province`] &&
                          !!props.errors[`${controlPrefix}_province`],
                        helperText:
                          props.touched[`${controlPrefix}_province`] &&
                          props.errors[`${controlPrefix}_province`],
                      }
                    : {})}
                  //   InputLabelProps={{ shrink: true }}
                  label={label}
                  size="small"
                />
              )}
            />
          )}
        />
      </FormControl>
      <FormControl className="col-span-2 lg:col-span-1">
        <SalifyFallback.Label
          key={formValue.province}
          path="District"
          default="Quận/Thành phố"
          render={(label) => (
            <Autocomplete
              options={districtOptions}
              fullWidth
              getOptionLabel={(option) => option?.label ?? ""}
              readOnly={readOnly}
              disableClearable
              value={districtValue}
              onChange={(_, { id }: any) => handleChangeDistrict(id)}
              renderInput={(params: any) => (
                <TextField
                  inputProps={{ readOnly }}
                  {...params}
                  {...(props.form
                    ? {
                        error:
                          !!props.touched[`${controlPrefix}_district`] &&
                          !!props.errors[`${controlPrefix}_district`],
                        helperText:
                          props.touched[`${controlPrefix}_district`] &&
                          props.errors[`${controlPrefix}_district`],
                      }
                    : {})}
                  //   InputLabelProps={{ shrink: true }}
                  label={label}
                  size="small"
                />
              )}
            />
          )}
        />
      </FormControl>
      <FormControl className="col-span-2 lg:col-span-1">
        <SalifyFallback.Label
          key={formValue.district}
          path="Ward"
          default="Phường"
          render={(label) => (
            <Autocomplete
              options={wardOptions}
              fullWidth
              getOptionLabel={(option) => option?.label ?? ""}
              readOnly={readOnly}
              disableClearable
              value={wardValue}
              onChange={(_, { id }: any) => handleChangeWard(id)}
              renderInput={(params: any) => (
                <TextField
                  inputProps={{ readOnly }}
                  {...params}
                  {...(props.form
                    ? {
                        error:
                          !!props.touched[`${controlPrefix}_ward`] &&
                          !!props.errors[`${controlPrefix}_ward`],
                        helperText:
                          props.touched[`${controlPrefix}_ward`] &&
                          props.errors[`${controlPrefix}_ward`],
                      }
                    : {})}
                  //   InputLabelProps={{ shrink: true }}
                  label={label}
                  size="small"
                />
              )}
            />
          )}
        />
      </FormControl>
    </>
  );
};

export default CartLocationControls;
